import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Envelope } from '@phosphor-icons/react';
import { useTheme } from 'context/ThemeContext';
import { useToast } from 'context/ToastContext';
import { PODCAST_PROVIDER_MAP } from 'shared/data/podcastProviders';
import { Button } from '../Button/Button';
import { SOCIALS_MAP } from 'shared/data/socialMedia';
import { hasPublishedEpisodes } from 'shared/utils/getEpisode';
import { CONTACT_EMAIL } from 'shared/constants/common';

const LINK_COLUMNS = [
  {
    titleKey: 'footer.social' as const,
    links: Object.values(SOCIALS_MAP),
  },
  {
    titleKey: 'footer.listen' as const,
    links: Object.values(PODCAST_PROVIDER_MAP),
  },
  // {
  //   title: 'Editorial',
  //   links: [
  //     { label: 'Newsletter', href: '#' },
  //     { label: 'Media Kit', href: '#' },
  //   ],
  // },
  // {
  //   title: 'Legal',
  //   links: [
  //     { label: 'Privacy', href: '#' },
  //     { label: 'Terms', href: '#' },
  //   ],
  // },
];

export function Footer() {
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const { showToast } = useToast();

  const linkColumns = hasPublishedEpisodes() ? LINK_COLUMNS : [LINK_COLUMNS[0]]

  const handleCopy = async (href: string) => {
    try {
      await navigator.clipboard.writeText(href);
      showToast({
        variant: 'success',
        title: t('toast.rssCopied.title'),
        message: t('toast.rssCopied.message'),
      });
    } catch {
      showToast({
        variant: 'error',
        title: t('toast.rssCopyFailed.title'),
        message: t('toast.rssCopyFailed.message'),
      });
    }
  };

  const linkClasses =
    'font-sans text-sm font-light text-on-surface-variant/70 hover:text-tertiary transition-all text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tertiary focus-visible:ring-offset-2 rounded-sm';

  return (
    <footer className="bg-surface-container-lowest w-full py-12 md:py-20 px-6 lg:px-12">
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-start gap-12">
        {/* Brand */}
        <div className="max-w-md lg:flex-shrink-0">
          <Link to="/" className="flex items-center gap-3 mb-4">
            <img
              src="/mmampod_logo_no_background.png"
              alt={t('podcast.title')}
              className="w-10 h-10 rounded-full"
            />
            <span className="font-serif italic text-tertiary text-3xl">
              {t('podcast.title')}
            </span>
          </Link>
          <p className="font-sans text-sm font-light text-on-surface-variant/70 leading-relaxed">
            {t('podcast.blurb')}
          </p>
        </div>

        {/* Links + Contact */}
        <div className="flex items-start justify-between gap-6 w-full lg:flex-1">
          <div className="grid grid-cols-2 sm:grid-cols-2 gap-x-12 gap-y-8">
            {linkColumns.map((column) => {
              return (
                <div key={column.titleKey}>
                  <span className="col-span-2 font-sans text-xs uppercase tracking-widest text-tertiary font-bold">
                    {t(column.titleKey)}
                  </span>
                  <div className="flex flex-col gap-4 mt-4">
                    {column.links.map((link) =>
                      link.copyable ? (
                        <button
                          key={link.label}
                          type="button"
                          onClick={() => handleCopy(link.href)}
                          className={linkClasses}
                        >
                          {link.label}
                        </button>
                      ) : (
                        <a
                          key={link.label}
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={linkClasses}
                        >
                          {link.label}
                          <span className="sr-only">{t('a11y.opensInNewTab')}</span>
                        </a>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Contact */}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            aria-label={t('footer.contactUs')}
            className="rounded-full p-3 bg-surface-container text-on-surface-variant/80 hover:text-tertiary hover:scale-[1.02] transition-all flex items-center justify-center focus-visible:ring-2 focus-visible:ring-tertiary focus-visible:ring-offset-2 focus-visible:outline-none flex-shrink-0"
          >
            <Envelope size={20} />
          </a>
        </div>
      </div>

      {process.env.NODE_ENV === 'development' && (
        <div className="max-w-7xl mx-auto mt-12 pt-12 border-t border-outline-variant/10 flex items-center justify-between">
          <Button
            variant="link"
            size="sm"
            icon={theme === 'dark' ? 'sun' : 'moon'}
            onClick={toggleTheme}
            aria-label={t('footer.toggleTheme')}
            className="text-on-surface-variant/50 hover:text-tertiary font-light"
          >
            <span className="hidden sm:inline">{theme === 'dark' ? t('footer.lightMode') : t('footer.darkMode')}</span>
          </Button>
        </div>
      )}
    </footer>
  );
}
